import { FC } from "react";
import { Box, Stack, Typography, styled } from "@mui/material";
import { background, text, blue, red, black, divider } from "@/theme/colors";
import { IconTrendUp, IconTrendDown } from "@/assets/icons";
import { TAnnualSummary } from "@/api/services/main/dashboard/dtos/performance-detail.dto";
import { LoadingArea } from "@/components/elements/Loading/Loading";

type TAnnualSummaryCardsProps = {
  data?: TAnnualSummary;
  isLoading?: boolean;
};

type TCardItem = {
  label: string;
  value: number;
  prevValue?: number;
  unit: string;
};

const YearComparison: FC<{ current: number; previous: number }> = ({ current, previous }) => {
  const difference = current - previous;
  if (difference === 0) return null;

  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      {difference > 0 && <IconTrendUp />}
      {difference < 0 && <IconTrendDown />}
      <Typography variant="cap12Bold" color={difference > 0 ? blue[80] : red[85]}>
        {difference > 0 ? "+" : ""}
        {difference.toLocaleString()}
      </Typography>
      <Typography variant="cap11Light" color={text.secondary}>
        前年比
      </Typography>
    </Stack>
  );
};

export const AnnualSummaryCards: FC<TAnnualSummaryCardsProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return <LoadingArea />;
  }

  const topCards: TCardItem[] = [
    { label: "新規求人数", value: data?.newJobs ?? 0, prevValue: data?.newJobsPrevYear, unit: "件" },
    { label: "新規登録人材数", value: data?.newTalents ?? 0, prevValue: data?.newTalentsPrevYear, unit: "人" },
  ];

  const bottomCards: TCardItem[] = [
    { label: "推薦前数", value: data?.beforeProposals ?? 0, prevValue: data?.beforeProposalsPrevYear, unit: "件" },
    { label: "推薦数", value: data?.proposals ?? 0, prevValue: data?.proposalsPrevYear, unit: "件" },
    { label: "面接数", value: data?.interviews ?? 0, prevValue: data?.interviewsPrevYear, unit: "件" },
    { label: "内定数", value: data?.offers ?? 0, prevValue: data?.offersPrevYear, unit: "件" },
    { label: "内定受諾数", value: data?.offerAccepted ?? 0, prevValue: data?.offerAcceptedPrevYear, unit: "件" },
  ];

  return (
    <Stack gap={2}>
      <SummaryContainer direction="row">
        {topCards.map((card, index) => (
          <SectionItem key={card.label} $hasDivider={index !== topCards.length - 1}>
            <LabelArea>
              <Typography variant="cap12" color={text.secondary}>
                {card.label}
              </Typography>
            </LabelArea>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1.5}>
              <Stack direction="row" alignItems="baseline" gap={0.5}>
                <Typography variant="head24Bold" color={text.primary} sx={{ fontSize: "28px", lineHeight: "34px" }}>
                  {card.value.toLocaleString()}
                </Typography>
                <Typography variant="body14" color={text.primary}>
                  {card.unit}
                </Typography>
              </Stack>
              {card.prevValue !== undefined && <YearComparison current={card.value} previous={card.prevValue} />}
            </Stack>
          </SectionItem>
        ))}
      </SummaryContainer>

      <SummaryContainer direction="row">
        {bottomCards.map((card, index) => (
          <SectionItem key={card.label}>
            {index !== bottomCards.length - 1 && <DividerArrow />}
            <LabelArea>
              <Typography variant="cap12" color={text.secondary}>
                {card.label}
              </Typography>
            </LabelArea>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1.5}>
              <Stack direction="row" alignItems="baseline" gap={0.5}>
                <Typography variant="head24Bold" color={text.primary}>
                  {card.value.toLocaleString()}
                </Typography>
                <Typography variant="body14" color={text.primary}>
                  {card.unit}
                </Typography>
              </Stack>
              {card.prevValue !== undefined && <YearComparison current={card.value} previous={card.prevValue} />}
            </Stack>
          </SectionItem>
        ))}
      </SummaryContainer>
    </Stack>
  );
};

const SummaryContainer = styled(Stack)`
  border: 1px solid ${divider.middle};
  border-radius: 8px;
  background-color: ${background.white};
`;

const SectionItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$hasDivider",
})<{ $hasDivider?: boolean }>`
  flex: 1;
  padding: 16px 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  ${({ $hasDivider }) =>
    $hasDivider &&
    `
    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 16px;
      bottom: 16px;
      width: 1px;
      background-color: ${divider.middle};
    }
  `}
`;

const LabelArea = styled(Box)`
  padding-bottom: 10px;
  border-bottom: 1px solid ${divider.middle};
`;

const DividerArrow = styled(Box)`
  position: absolute;
  right: -5px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 5px solid ${black[80]};
  z-index: 1;
`;

